// Chips in a fixed-width cell (labor, non-labor, Space, Stock): only whole
// chips are ever shown, and every shown chip keeps its value (the labor
// units, the non-labor amount). When they do not all fit in full, the cell
// shows as many full chips as fit and a "+N" chip whose tooltip lists the
// rest with their values. Hiding the value of every chip to squeeze them all
// in read as a row of bare acronyms, so it is never done on a multi-chip row.
// A lone chip has nothing to count: it alone may lose its value, as a last
// resort, so the cell never overflows (SPEC-0022 chip anatomy).
// Widths are measured once per chip set (every chip rendered in full) and the
// fit is recomputed when the chip set or the cell width changes.
import { useLayoutEffect, useRef, useState } from "react";

export const CHIP_GAP_PX = 4;
// The "+N" chip: 6px padding each side, "+N" text, 1px border (resourceChip.scss).
export const MORE_CHIP_RESERVE_PX = 28;
export const FIT_LEVELS = 2;

// Container class for the lone-chip last resort (level 1; level 0 has none).
export const fitLevelClass = (level) => (level > 0 ? ` offolio-chip-row--fit-${level}` : "");

const BODY_SELECTOR = ".offolio-resource-chip__body";
const ACRONYM_SELECTOR = ".offolio-resource-chip__acronym";

// The row's content width: its own padding (room for the focus ring) is not
// room for chips. Matches the ResizeObserver's contentRect.
const contentWidthOf = (el) => {
  const style = typeof getComputedStyle === "function" ? getComputedStyle(el) : null;
  const padding = (parseFloat(style?.paddingLeft) || 0) + (parseFloat(style?.paddingRight) || 0);
  return Math.max(0, (el.clientWidth || 0) - padding);
};

// Layout width only: chips never shrink (flex: none), and a scrollWidth would
// count the labor remove control laid over the chip's corner.
const widthOf = (el) => (el ? el.offsetWidth || 0 : 0);

// [full, noValue] widths of one chip rendered at level 0. A chip without
// the anatomy (no acronym segment found) keeps one width at every level.
export const measureChip = (chip) => {
  const full = widthOf(chip);
  const acronym = chip.querySelector?.(ACRONYM_SELECTOR);
  const body = chip.querySelector?.(BODY_SELECTOR);
  if (!acronym || !body) return [full, full];
  return [full, Math.min(full, widthOf(acronym))];
};

const rowWidth = (items, level, n, gap) =>
  items.slice(0, n).reduce((sum, w, i) => sum + w[level] + (i ? gap : 0), 0);

// items: [full, noValue] per chip. Returns { count, level }; level 1 only
// ever comes with a lone chip.
// moreGap: the space between the last chip and the "+N" (the chip gap unless
// the caller already took it out of `available`).
export const fitChips = (items, available, { gap = CHIP_GAP_PX, reserve = MORE_CHIP_RESERVE_PX, moreGap = gap } = {}) => {
  const all = items.length;
  // No layout yet (hidden cell, jsdom): never hide anything.
  if (!(available > 0) || all === 0) return { count: all, level: 0 };
  if (rowWidth(items, 0, all, gap) <= available) return { count: all, level: 0 };
  if (all === 1) return { count: 1, level: FIT_LEVELS - 1 };
  const withMore = (n) => rowWidth(items, 0, n, gap) + (n ? moreGap : 0) + reserve <= available;
  // Never a half-cut chip, the "+N" included: when not even the first chip
  // fits beside it, the cell shows "+N" alone (its tooltip lists them all).
  let count = 0;
  while (count < all - 1 && withMore(count + 1)) count += 1;
  return { count, level: 0 };
};

// The fit is a pure function of (natural chip widths, cell width):
// - natural widths are measured once per chip set, while every chip is
//   rendered in full; they are never re-measured from the reduced rendering;
// - the cell width is read when the chip set changes and when the observer
//   reports a different width (one read per animation frame);
// - setState bails out on an equal fit, and a chip set that keeps changing
//   its fit (a content-sized cell whose width follows the fit) is frozen
//   after MAX_REFITS changes. Live check: React #185 came from the old
//   measure-after-every-render loop flipping 1 <-> N.
export const MAX_REFITS = 4;

const fitKey = ({ count, level }) => `${count}:${level}`;
const parseFit = (key) => {
  const [count, level] = key.split(":").map(Number);
  return { count, level };
};

// `contentKey` changes when a chip's text changes (value, badge), so its
// natural width is measured again. Returns { visible, level }.
export const useChipOverflow = (containerRef, keys, enabled = true, contentKey = "") => {
  const signature = `${keys.join("|")}#${contentKey}`;
  const [fit, setFit] = useState(() => fitKey({ count: keys.length, level: 0 }));
  const widths = useRef(new Map());
  const measuredFor = useRef(null);
  const refits = useRef({ signature: null, changes: 0 });
  const lastCellWidth = useRef(null);
  const keysRef = useRef(keys);
  keysRef.current = keys;

  const refit = (cellWidth) => {
    const measured = keysRef.current.map((k) => widths.current.get(String(k)));
    if (measured.some((w) => w == null)) return;
    const next = fitKey(fitChips(measured, cellWidth));
    const guard = refits.current;
    if (guard.signature !== measuredFor.current) {
      guard.signature = measuredFor.current;
      guard.changes = 0;
    }
    setFit((current) => {
      if (current === next) return current;
      if (guard.changes >= MAX_REFITS) return current;
      guard.changes += 1;
      return next;
    });
  };

  // New chip set: render it in full, then measure it once.
  useLayoutEffect(() => {
    if (!enabled) return;
    measuredFor.current = null;
    setFit(fitKey({ count: keys.length, level: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, enabled]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!enabled || !el || measuredFor.current === signature) return;
    if (fit !== fitKey({ count: keys.length, level: 0 })) return; // not in full yet
    const chips = el.querySelectorAll("[data-resource-id]");
    if (chips.length < keys.length) return; // not fully rendered yet
    chips.forEach((chip) => {
      const w = measureChip(chip);
      if (w[0] > 0) widths.current.set(chip.dataset.resourceId, w);
    });
    measuredFor.current = signature;
    const width = Math.round(contentWidthOf(el));
    lastCellWidth.current = width;
    refit(width);
  });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!enabled || !el || typeof ResizeObserver === "undefined") return undefined;
    let frame = null;
    let pending = null;
    const ro = new ResizeObserver((entries) => {
      pending = entries?.[0]?.contentRect?.width ?? contentWidthOf(el);
      if (frame != null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        const width = Math.round(pending);
        if (width === lastCellWidth.current) return;
        lastCellWidth.current = width;
        refit(width);
      });
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (frame != null) window.cancelAnimationFrame?.(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, enabled]);

  if (!enabled) return { visible: keys.length, level: 0 };
  const { count, level } = parseFit(fit);
  return { visible: count, level };
};
